import { F, O, RA, RR } from '@macs-frontend/core/utils/fp-ts';
import { isoUUID, UUID } from '@macs-frontend/core/utils/uuid';
import { ChannelTeam } from '@macs-frontend/features/shared/models/channel.model';
import { Channel, ChannelGroup, FinancialUtils } from '@macs-frontend/web-macs/shared';
import { EntityState } from '@ngrx/entity';
import { box, unbox } from 'ngrx-forms';
import {
  ChannelForm,
  ChannelGroupForm,
  ChannelsForm,
  ChannelTeamForm,
} from '../reducers/channels';

export class CSCalculations {
  static toChannelGroupForm(group: ChannelGroup): ChannelGroupForm {
    return {
      ...group,
      // @ts-expect-error
      channels: group.channels && group.channels.map(CSCalculations.toChannelForm),
    };
  }

  static toChannelForm(channel: Channel): ChannelForm {
    return {
      ...channel,
      actionTeams:
        channel.actionTeams && channel.actionTeams.map(CSCalculations.toActionTeamForm),
    };
  }

  static toActionTeamForm(team: ChannelTeam): ChannelTeamForm {
    return {
      ...team,
      scheduleAssignments: team.scheduleAssignments && team.scheduleAssignments.map(box),
      scheduleStartLocations:
        team.scheduleStartLocations && team.scheduleStartLocations.map(box),
      compositionRoles: team.compositionRoles && team.compositionRoles.map(box),
      feesBudgetItem: team.feesBudgetItem && box(team.feesBudgetItem),
      // @ts-expect-error
      action: team.feesBudgetItem && box(team.action),
      budgetItem: team.budgetItem && box(team.budgetItem),
    };
  }

  static toChannelGroup(group: ChannelGroupForm): ChannelGroup {
    return {
      ...group,
      channels: group.channels && group.channels.map(CSCalculations.toChannel),
    };
  }

  static toChannel(channel: ChannelForm): Channel {
    return {
      ...channel,
      actionTeams:
        channel.actionTeams && channel.actionTeams.map(CSCalculations.toActionTeam),
    };
  }

  static toActionTeam(team: ChannelTeamForm): ChannelTeam {
    return {
      ...team,
      // @ts-expect-error
      scheduleAssignments:
        team.scheduleAssignments && team.scheduleAssignments.map(unbox),
      // @ts-expect-error
      scheduleStartLocations:
        team.scheduleStartLocations && team.scheduleStartLocations.map(unbox),
      // @ts-expect-error
      compositionRoles: team.compositionRoles && team.compositionRoles.map(unbox),
      feesBudgetItem: team.feesBudgetItem && unbox(team.feesBudgetItem),
      action: team.feesBudgetItem && unbox(team.action),
      budgetItem: team.budgetItem && unbox(team.budgetItem),
    };
  }

  static calcChannels(
    currentChannelGroup: ChannelGroupForm,
    selectedChannels: ChannelForm[],
    totalContacts: number,
    teamBudget: number,
    maxBudget: number,
    targetGroupFilterLength: number,
  ) {
    const otherChannels: ChannelForm[] = selectedChannels.filter(
      c => c.channelGroup.uuid !== currentChannelGroup.uuid,
    );
    const allChannels: ChannelForm[] = [
      ...currentChannelGroup.channels,
      ...otherChannels,
    ];
    const budgetCase = maxBudget > 0;
    const contactsCase = totalContacts > 0;
    return (
      allChannels
        // eslint-disable-next-line sonarjs/cognitive-complexity
        .map(channel => {
          const channelActions = channel.actions || 0;
          const ccBillable = FinancialUtils.roundToCents(
            channel.teamCost / channel.contactsPerAction,
          );
          const ccReal = FinancialUtils.roundToCents(
            // @ts-expect-error
            channel.realCost / channel.contactsPerAction,
          );
          const channelContacts = channel.contactsPerAction * +channelActions;
          // @ts-expect-error
          const extraCost = channel.actionTeams.length
            ? // @ts-expect-error
              channel.actionTeams.reduce((prev, team) => prev + (team.extraCost || 0), 0)
            : channel.extraCost;
          const channelBudget = extraCost
            ? channel.teamCost * channelActions + extraCost * channelActions
            : channel.teamCost * channelActions;

          const totalBudget = allChannels
            .filter(c => c.uuid !== channel.uuid)
            .reduce((sum, c) => {
              // @ts-expect-error
              const ateamSum = c.actionTeams.reduce(
                (asum, ateam) =>
                  // @ts-expect-error
                  asum + ateam.budgetVal,
                0,
              );
              // @ts-expect-error
              const _extraCost = c.actionTeams.reduce(
                (prev, team) => prev + (team.extraCost || 0),
                0,
              );
              const newChannelBudget = _extraCost
                ? // @ts-expect-error
                  c.teamCost * c.actions + _extraCost * c.actions
                : // @ts-expect-error
                  c.teamCost * c.actions;
              return (
                // @ts-expect-error
                (c.actionTeams.length > 0 ? ateamSum : newChannelBudget) + sum
              );
            }, 0);

          const estTotalContacts = selectedChannels.reduce((sum, c) => {
            // @ts-expect-error
            const ateamSum = c.actionTeams.reduce(
              (asum, ateam) =>
                // @ts-expect-error
                asum + ateam.totalContactsVal,
              0,
            );
            return (
              ((c.actionTeams?.length || 0) > 0
                ? ateamSum
                : (c.actions || 0) * c.contactsPerAction) + sum
            );
          }, 0);

          // @ts-expect-error
          // eslint-disable-next-line functional/no-let
          let budget: number = null;
          if (contactsCase && budgetCase) {
            budget = maxBudget;
          } else if (contactsCase) {
            budget = totalBudget + channelBudget;
          } else if (budgetCase) {
            budget = teamBudget;
          }

          // @ts-expect-error
          const actionTeams: ChannelTeamForm[] = channel.actionTeams.map(ateam => {
            const ccTeamBillable = ateam.billablePrice
              ? FinancialUtils.roundToCents(
                  ateam.billablePrice / channel.contactsPerAction,
                )
              : 0;
            const ccTeamReal = FinancialUtils.fromPercents(
              ccTeamBillable,
              100 - ateam.margin,
            );
            const channelTeamContacts = channel.contactsPerAction * (ateam.actions || 0);
            // @ts-expect-error
            const otherTeamsBudget = channel.actionTeams
              .filter(t => t.uuid !== ateam.uuid)
              .reduce(
                (sum, team) =>
                  sum +
                  (team.extraCost
                    ? // @ts-expect-error
                      team.billablePrice * team.actions +
                      team.extraCost * (team.actions || 0)
                    : // @ts-expect-error
                      team.billablePrice * team.actions),
                0,
              );
            const channelTeamBudget = ateam.extraCost
              ? // @ts-expect-error
                ateam.billablePrice * ateam.actions +
                ateam.extraCost * (ateam.actions || 0)
              : // @ts-expect-error
                ateam.billablePrice * ateam.actions;
            if (contactsCase && budgetCase) {
              budget = maxBudget;
            } else if (contactsCase) {
              budget = totalBudget + channelTeamBudget + otherTeamsBudget;
            } else if (budgetCase) {
              budget = teamBudget;
            }
            return {
              ...ateam,
              ccBillable: ccTeamBillable !== Infinity ? ccTeamBillable : 0,
              ccReal: ccTeamReal !== Infinity ? ccTeamReal : 0,
              targetContacts: targetGroupFilterLength
                ? FinancialUtils.fromPercents(
                    channel.contactsPerAction,
                    channel.matches[0] ? channel.matches[0].match : 0,
                    // @ts-expect-error
                  ) * ateam.actions
                : 0,
              totalContactsVal: channelTeamContacts,
              totalContactsPercent: FinancialUtils.toPercents(
                totalContacts > 0 ? totalContacts : estTotalContacts,
                channelTeamContacts,
              ),
              budgetVal: channelTeamBudget,
              budgetPercent: FinancialUtils.toPercents(budget, channelTeamBudget),
              feesBudgetItem: box({
                ...unbox(ateam.feesBudgetItem),
              }),
            };
          });

          const avgTeamCost: number = actionTeams.length
            ? actionTeams.reduce((sum, aTeam) => sum + aTeam.billablePrice, 0) /
              actionTeams.length
            : channel.teamCost;
          const avgCostPerContact: number = actionTeams.length
            ? // @ts-expect-error
              actionTeams.reduce((sum, aTeam) => sum + aTeam.ccBillable, 0) /
              channel.contactsPerAction
            : ccBillable;

          return {
            ...channel,
            ccBillable: ccBillable !== Infinity ? ccBillable : 0,
            ccReal: ccReal !== Infinity ? ccReal : 0,
            targetContacts: targetGroupFilterLength
              ? FinancialUtils.fromPercents(
                  channel.contactsPerAction,
                  channel.matches[0] ? channel.matches[0].match : 0,
                ) * channelActions
              : 0,
            totalContactsVal: channelContacts,
            totalContactsPercent: FinancialUtils.toPercents(
              totalContacts > 0 ? totalContacts : estTotalContacts,
              channelContacts,
            ),
            budgetVal: channelBudget,
            budgetPercent: FinancialUtils.toPercents(budget, channelBudget),
            actionTeams,
            avgTeamCost,
            avgCostPerContact,
          };
        })
        .filter(channel => channel.channelGroup.uuid === currentChannelGroup.uuid)
    );
  }

  static getChannelsActionsCount(
    channelGroups: ChannelGroupForm[],
    selectedChannels: EntityState<ChannelForm>,
  ): number {
    return channelGroups.reduce(addChannelGroupActions, 0);

    function addChannelGroupActions(
      totalActions: number,
      group: ChannelGroupForm,
    ): number {
      return group.channels
        ? totalActions + group.channels.reduce(addChannelActions, 0)
        : totalActions;
    }

    function addChannelActions(totalActions: number, channel: ChannelForm): number {
      if (selectedChannels.entities[isoUUID.unwrap(channel.uuid)]) {
        // @ts-expect-error
        return channel.actionTeams.length
          ? // @ts-expect-error
            totalActions + channel.actionTeams.reduce(addActionTeamActions, 0)
          : selectedChannels.entities[isoUUID.unwrap(channel.uuid)]
          ? // @ts-expect-error
            !isNaN(+channel.actions)
            ? // @ts-expect-error
              totalActions + +channel.actions
            : totalActions
          : totalActions;
      }
      return totalActions;
    }

    function addActionTeamActions(totalActions: number, team: ChannelTeamForm): number {
      // @ts-expect-error
      return totalActions + +team.actions;
    }
  }

  static getAllActionTeams(channelGroups: ChannelGroupForm[]): ChannelTeamForm[] {
    return channelGroups.reduce(
      (totalActionTeams: ChannelTeamForm[], channelGroup: ChannelGroupForm) => {
        const actionTeams: ChannelTeamForm[] = totalActionTeams;

        return channelGroup.channels.reduce(
          (teams, channel: ChannelForm) => [...teams, ...(channel.actionTeams || [])],
          actionTeams,
        );
      },
      [],
    );
  }

  static findChannelForm(
    channelUuid: UUID,
    channelsForm: ChannelsForm,
  ): O.Option<ChannelForm> {
    return F.pipe(
      channelsForm.channelGroups.entities,
      RR.toReadonlyArray,
      RA.findFirstMap(([, group]) =>
        F.pipe(
          group,
          O.fromNullable,
          O.filterMap(group =>
            F.pipe(
              group.channels,
              RA.findFirst(channel => channel.uuid === channelUuid),
            ),
          ),
        ),
      ),
    );
  }
}
